import React, { useState, useEffect, useRef, Suspense } from "react";
import axios from "axios";
import AOS from "aos";
import { useEffectOnce } from "react-use";

// Dynamically import OwlCarousel with Suspense
const OwlCarousel = React.lazy(() => import("react-owl-carousel"));

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [introText, setIntroText] = useState("");
  const [testimonialImage, setTestimonialImage] = useState("");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBrowser, setIsBrowser] = useState(false);
  const carouselRef = useRef(null);

  const BASE_URL = "https://admin.shapeshiftingmastery.ca";
  const API_URL_TESTIMONIALS = `${BASE_URL}/api/v1/topics/11/page/1/count/50`;
  const API_URL_INTRO_TEXT = `${BASE_URL}/api/v1/topic/177`;
  const API_URL_PHOTO = `${BASE_URL}/api/v1/topic/photos/180`;

  // Load jQuery and OwlCarousel dynamically on the client side
  useEffectOnce(() => {
    const loadDependencies = async () => {
      if (typeof window !== "undefined") {
        setIsBrowser(true);
        try {
          if (!window.jQuery) {
            const $ = (await import("jquery")).default;
            window.$ = $;
            window.jQuery = $;
          }

          if (!window.jQuery.fn?.owlCarousel) {
            await import("owl.carousel/dist/assets/owl.carousel.css");
            await import("owl.carousel/dist/assets/owl.theme.default.css");
            await import("owl.carousel");
          }
        } catch (err) {
          console.error("Error loading jQuery or OwlCarousel", err);
        }
      }
    };
    loadDependencies();
  }, []);

  // Fetch API Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, introTextRes, photoRes] = await Promise.all([
          axios.get(API_URL_TESTIMONIALS),
          axios.get(API_URL_INTRO_TEXT),
          axios.get(API_URL_PHOTO),
        ]);

        setTestimonials(testimonialsRes.data.topics || []);

        const introTitle = introTextRes.data.topic[0]?.fields.find(
          (field) => field.title === "Component-title"
        );
        setTitle(introTitle?.value || "Testimonials");
        setIntroText(introTextRes.data.topic[0]?.details || "");
        setTestimonialImage(photoRes.data.topic_photo || "");
      } catch (err) {
        setError(err.message || "Error fetching data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Initialize OwlCarousel
  useEffect(() => {
    if (!isBrowser || !carouselRef.current) return;

    const initializeCarousel = async () => {
      try {
        if (!window.jQuery?.fn?.owlCarousel) {
          console.warn("jQuery or OwlCarousel not ready yet.");
          return;
        }

        window.jQuery(carouselRef.current).owlCarousel({
          loop: true,
          margin: 10,
          nav: true,
          dots: true,
          autoplay: true,
          autoplayTimeout: 5000,
          autoplayHoverPause: true,
          responsive: {
            0: { items: 1 },
            600: { items: 2 },
            1000: { items: 3 },
          },
        });
      } catch (error) {
        console.error("Failed to initialize carousel:", error);
      }
    };

    initializeCarousel();

    // Cleanup
    return () => {
      if (carouselRef.current && window.jQuery?.fn?.owlCarousel) {
        window.jQuery(carouselRef.current).owlCarousel("destroy");
      }
    };
  }, [isBrowser]);

  if (error) return <div className="error-message">{error}</div>;
  if (loading) return <div className="loading">Loading testimonials...</div>;

  return (
    <section className="sec_padding testimonial">
      <div className="container">
        <div className="row overflow-hidden">
          <div className="col-md-6 col-lg-7">
            <div className="content-top pr-md-5 wow animate__animated animate__fadeInLeft" data-aos="fade-right">
              <h6 className="sub_heading">{title}</h6>
              <h2 className="sec_heading-stylish">Life Empowered Experiences!</h2>
              <p
                className="title-pera pt-2 pb-3"
                dangerouslySetInnerHTML={{ __html: introText }}
              />
            </div>

            <div className="testimonial-slide wow animate__animated animate__slideInLeft" data-aos="zoom-in-right">
              {isBrowser && (
                <Suspense fallback={<div>Loading Carousel...</div>}>
                  <OwlCarousel
                    className="owl-theme"
                    loop={true}
                    margin={10}
                    nav={true}
                    dots={false}
                    autoplay={true}
                    autoplayTimeout={3000}
                    autoplayHoverPause={true}
                    items={1}
                    navText={[
                      '<span class="owl-prev"><i class="fa fa-chevron-left"></i></span>',
                      '<span class="owl-next"><i class="fa fa-chevron-right"></i></span>',
                    ]}
                  >
                    {testimonials.map((testimonial, index) => (
                      <div className="item-testimonial" key={index}>
                        <p className="pera-testimonial">
                          {testimonial.details || "No testimonial content"}
                        </p>
                        <div className="user-box">
                          <div className="img-user">
                            <img
                              src={
                                testimonial.photo_file ||
                                "/assets/images/user/user-default.png"
                              }
                              alt={testimonial.title || "Anonymous User"}
                            />
                          </div>
                          <div className="content">
                            <h5 className="user-name">
                              {testimonial.title || "Anonymous"}
                            </h5>
                            <div className="rating-icon">
                              {[...Array(5)].map((_, i) => (
                                <i className="fa-solid fa-star" key={i}></i>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </OwlCarousel>
                </Suspense>
              )}
            </div>
          </div>
          <div className="col-md-6 col-lg-5">
            <div className="img-testimonial wow animate__animated animate__slideInRight" data-aos="zoom-in-left">
              <img
                src={
                  testimonialImage || "/assets/images/testimonial-default.jpg"
                }
                alt="Testimonials"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
